/**
 * EmailAttachments
 *
 * Display-only list of an email's attachments (paperclip + filename + size).
 * We only store attachment metadata, not the file content, so these are
 * non-downloadable indicators - shared by the portal and staff email views.
 */

import { Paperclip } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatFileSize } from '@/utils/formatters';
import type { InvestorEmailLogAttachment } from '@/types/investorEmail';

export function EmailAttachments({
  attachments,
  className,
}: {
  attachments?: InvestorEmailLogAttachment[] | null;
  className?: string;
}) {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {attachments.map((attachment) => (
        <div
          key={attachment.path ?? attachment.filename}
          className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5 text-sm"
          title={attachment.filename}
        >
          <Paperclip className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="max-w-[220px] truncate font-medium text-foreground">
            {attachment.filename}
          </span>
          {attachment.size != null && (
            <span className="flex-shrink-0 text-xs text-muted-foreground">
              {formatFileSize(attachment.size)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
